/**
 * Yönetici Dashboard — Spec Bölüm 8.5 (BMU Index) + S4.1
 *
 * Tam metrikler:
 *  - Aktif firma + multi-org switcher
 *  - Count cards (sınav/aday/sertifika)
 *  - BMU Index card (sektör karşılaştırma)
 *  - Konu ortalamaları bar chart
 *  - Level dağılımı
 *  - Son sınavlar + son tamamlananlar + aktivite feed
 *  - 3 hızlı aksiyon kartı
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { Header } from '../components/Header.js';
import { Alert, Card } from '../components/ui.js';
import { ChatWidget } from '../components/ChatWidget.js';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/auth-client.js';
import { trackEngagement } from '../lib/engagement.js';

interface DashboardData {
  organization: {
    id: string;
    name: string;
    industry: string;
    teamSize: number | null;
    bmuLeadStatus: string;
    createdAt: string;
  } | null;
  currentMember: { role: 'OWNER' | 'ADMIN' | 'VIEWER' };
  counts: {
    exams: number;
    attempts: number;
    completedAttempts: number;
    certificates: number;
  };
  teamAverage: number | null;
  levelCounts: Record<string, number>;
  topicAverages: Record<string, number>;
  bmuIndex: {
    firmScore: number | null;
    sectorAverage: number | null;
    sectorTop10: number | null;
    deltaFromAverage: number | null;
    pointsToTop10: number | null;
    sampleSize: number;
  };
  recentActivity: Array<{
    id: string;
    eventType: string;
    createdAt: string;
    metadata: Record<string, unknown> | null;
  }>;
  recentExams: Array<{
    id: string;
    accessCode: string;
    durationMin: number;
    questionCount: number;
    anonymousMode: boolean;
    validUntil: string;
    createdAt: string;
    _count: { attempts: number };
  }>;
  recentCompletions: Array<{
    attemptId: string;
    score: number | null;
    level: string | null;
    completedAt: string | null;
    hasCertificate: boolean;
    displayName: string;
  }>;
}

interface OrgListItem {
  id: string;
  name: string;
  industry: string;
  role: string;
  isActive: boolean;
}

const TOPIC_LABELS: Record<string, string> = {
  PM: 'Parça Modelleme',
  AS: 'Assembly',
  DR: 'Drawing',
  SK: 'Sketch',
  SM: 'Sac Metal',
  WD: 'Weldments',
  SF: 'Surfacing',
  CF: 'Configurations',
  GN: 'Genel SW',
  DT: 'Tasarım Teorisi',
};

const LEVEL_LABELS: Record<string, string> = {
  L0: 'Tanışma',
  L1_MINUS: 'Başlangıç',
  L1: 'CSWA',
  L2: 'CSWA+',
  L3: 'CSWP',
  L4: 'İleri',
};

const LEVEL_COLORS: Record<string, string> = {
  L0: '#94a3b8',
  L1_MINUS: '#fb923c',
  L1: '#eab308',
  L2: '#0369a1',
  L3: '#15803d',
  L4: '#7e22ce',
};

const EVENT_LABELS: Record<string, string> = {
  exam_created: 'Sınav oluşturuldu',
  exam_started: 'Aday sınava başladı',
  exam_resumed: 'Aday sınava devam etti',
  exam_completed: 'Aday sınavı tamamladı',
  exam_expired: 'Sınav süresi doldu',
  member_invited: 'Ekip üyesi davet edildi',
  member_invitation_accepted: 'Ekip üyesi katıldı',
  member_role_changed: 'Üye rolü değişti',
  member_removed: 'Üye çıkarıldı',
  organization_updated: 'Firma bilgileri güncellendi',
};

export function ManagerDashboardPage(): JSX.Element {
  const [, setLocation] = useLocation();
  const { data: session, isPending: sessionPending } = useSession();
  const [data, setData] = useState<DashboardData | null>(null);
  const [orgs, setOrgs] = useState<OrgListItem[]>([]);
  const [activeOrgId, setActiveOrgId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    try {
      const [dashboard, orgList] = await Promise.all([
        api.get<DashboardData>('/api/manager/dashboard'),
        api.get<{ organizations: OrgListItem[]; activeOrgId: string | null }>(
          '/api/manager/organizations',
        ),
      ]);
      setData(dashboard);
      setOrgs(orgList.organizations);
      setActiveOrgId(orgList.activeOrgId);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'FORBIDDEN' && err.message.includes('Aktif firma yok')) {
          setLocation('/yonetici/firma-olustur');
          return;
        }
        setError(err.message);
      } else {
        setError('Veriler yüklenemedi');
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (sessionPending) return;
    if (!session?.user) {
      setLocation('/yonetici/giris');
      return;
    }
    void refresh();
    trackEngagement('dashboard_visit');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, sessionPending]);

  async function switchOrg(orgId: string): Promise<void> {
    setLoading(true);
    try {
      await api.post(`/api/manager/organizations/${orgId}/switch`);
      await refresh();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    }
  }

  if (sessionPending || loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-6xl px-6 py-12">
          <p className="text-slate-500">Yükleniyor…</p>
        </main>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-6xl px-6 py-12">
          <Alert variant="danger">{error ?? 'Bilinmeyen hata'}</Alert>
        </main>
      </div>
    );
  }

  const totalLevels = Object.values(data.levelCounts).reduce(
    (s, c) => s + c,
    0,
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-6xl px-6 py-12">
        {/* Org header + switcher */}
        <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-slate-500">Aktif Firma</p>
            <h1 className="text-3xl font-bold text-slate-900">
              {data.organization?.name ?? 'Firma yok'}
            </h1>
          </div>
          {orgs.length > 1 && (
            <select
              value={activeOrgId ?? ''}
              onChange={(e) => switchOrg(e.target.value)}
              className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} ({o.role})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Count cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <CountCard
            icon="📝"
            label="Sınav"
            value={data.counts.exams}
            href="/yonetici/sinavlar"
          />
          <CountCard
            icon="👥"
            label="Toplam Aday"
            value={data.counts.attempts}
          />
          <CountCard
            icon="✅"
            label="Tamamlanan"
            value={data.counts.completedAttempts}
          />
          <CountCard
            icon="🏅"
            label="Sertifika"
            value={data.counts.certificates}
          />
        </div>

        {/* BMU Index */}
        {data.teamAverage !== null && (
          <Card className="mt-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  BMU Index — Sektör Karşılaştırma
                </h2>
                <p className="mt-1 text-xs text-slate-500">
                  Spec 8.5: {data.organization?.industry} sektöründe
                  konumunuz
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-wider text-slate-500">
                  Ekip Ortalaması
                </p>
                <p className="text-4xl font-bold text-blue-900">
                  {data.teamAverage}
                  <span className="text-base text-slate-400">/100</span>
                </p>
              </div>
            </div>

            {data.bmuIndex.sectorAverage !== null ? (
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                <Detail
                  label="Sektör Ortalaması"
                  value={`${data.bmuIndex.sectorAverage}/100`}
                  hint={`${data.bmuIndex.sampleSize} attempt`}
                />
                <Detail
                  label="Sektör Top %10"
                  value={
                    data.bmuIndex.sectorTop10 !== null
                      ? `${data.bmuIndex.sectorTop10}/100`
                      : '—'
                  }
                />
                <Detail
                  label={
                    (data.bmuIndex.deltaFromAverage ?? 0) >= 0
                      ? 'Sektör Üzerinde'
                      : 'Sektör Altında'
                  }
                  value={
                    data.bmuIndex.deltaFromAverage !== null
                      ? `${data.bmuIndex.deltaFromAverage > 0 ? '+' : ''}${data.bmuIndex.deltaFromAverage}%`
                      : '—'
                  }
                  highlight={(data.bmuIndex.deltaFromAverage ?? 0) >= 0}
                />
              </div>
            ) : (
              <div className="mt-4">
                <Alert variant="info">
                  Sektörünüzde henüz yeterli karşılaştırma verisi yok (en az 5
                  firma gerekli). Daha fazla firma değerlendirme yaptıkça BMU
                  Index burada görünecek.
                </Alert>
              </div>
            )}

            {data.bmuIndex.pointsToTop10 !== null &&
              data.bmuIndex.pointsToTop10 > 0 && (
                <p className="mt-3 rounded-lg bg-amber-50 px-4 py-2 text-sm text-amber-900">
                  <strong>Top %10'a:</strong> +{data.bmuIndex.pointsToTop10} puan
                </p>
              )}
          </Card>
        )}

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          {/* Konu ortalamaları */}
          {Object.keys(data.topicAverages).length > 0 && (
            <Card>
              <h2 className="text-lg font-semibold text-slate-900">
                Konu Ortalamaları
              </h2>
              <div className="mt-4 space-y-2">
                {Object.entries(data.topicAverages)
                  .sort(([, a], [, b]) => b - a)
                  .map(([topic, pct]) => (
                    <div key={topic}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">
                          {TOPIC_LABELS[topic] ?? topic}
                        </span>
                        <span className="font-mono text-slate-900">{pct}</span>
                      </div>
                      <div className="mt-1 h-2 rounded-full bg-slate-100">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${pct}%`,
                            background:
                              pct >= 75
                                ? '#15803d'
                                : pct >= 50
                                ? '#0369a1'
                                : pct >= 30
                                ? '#ca8a04'
                                : '#dc2626',
                          }}
                        />
                      </div>
                    </div>
                  ))}
              </div>
            </Card>
          )}

          {/* Level dağılımı */}
          {totalLevels > 0 && (
            <Card>
              <h2 className="text-lg font-semibold text-slate-900">
                Yetkinlik Seviye Dağılımı
              </h2>
              <div className="mt-4 flex h-3 overflow-hidden rounded-full bg-slate-100">
                {Object.entries(data.levelCounts).map(([level, count]) => {
                  if (count === 0) return null;
                  const pct = (count / totalLevels) * 100;
                  return (
                    <div
                      key={level}
                      style={{
                        width: `${pct}%`,
                        background: LEVEL_COLORS[level] ?? '#94a3b8',
                      }}
                      title={`${LEVEL_LABELS[level]}: ${count}`}
                    />
                  );
                })}
              </div>
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {Object.entries(data.levelCounts).map(([level, count]) => (
                  <div key={level} className="flex items-center gap-2 text-sm">
                    <span
                      className="h-3 w-3 rounded-full"
                      style={{ background: LEVEL_COLORS[level] ?? '#94a3b8' }}
                    />
                    <span className="text-slate-700">
                      {level} · {LEVEL_LABELS[level]}
                    </span>
                    <span className="ml-auto font-mono text-slate-900">
                      {count}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          {/* Son tamamlananlar */}
          <Card>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">
                Son Tamamlananlar
              </h2>
              {data.recentCompletions.length > 0 && (
                <Link
                  href={`/yonetici/sinavlar`}
                  className="text-sm font-medium text-blue-900 hover:underline"
                >
                  Tümü
                </Link>
              )}
            </div>
            {data.recentCompletions.length === 0 ? (
              <p className="mt-3 text-sm text-slate-500">
                Henüz tamamlanan sınav yok.
              </p>
            ) : (
              <ul className="mt-4 space-y-3">
                {data.recentCompletions.map((c) => (
                  <li
                    key={c.attemptId}
                    className="flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-900">
                        {c.displayName}
                      </p>
                      <p className="text-xs text-slate-500">
                        {c.completedAt
                          ? new Date(c.completedAt).toLocaleString('tr-TR')
                          : '—'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-blue-900">{c.score}/100</p>
                      <p className="text-xs text-slate-500">
                        {c.level}
                        {c.hasCertificate && ' 🏅'}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Son sınavlar */}
          <Card>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">
                Son Sınavlar
              </h2>
              <Link
                href="/yonetici/sinavlar/yeni"
                className="text-sm font-medium text-blue-900 hover:underline"
              >
                + Yeni
              </Link>
            </div>
            {data.recentExams.length === 0 ? (
              <p className="mt-3 text-sm text-slate-500">
                Henüz sınav yok.{' '}
                <Link
                  href="/yonetici/sinavlar/yeni"
                  className="font-medium text-blue-900 hover:underline"
                >
                  İlk sınavı oluşturun
                </Link>
              </p>
            ) : (
              <ul className="mt-4 space-y-3">
                {data.recentExams.map((e) => (
                  <li key={e.id} className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <Link
                        href={`/yonetici/sinavlar/${e.id}`}
                        className="block truncate font-mono text-sm font-medium text-blue-900 hover:underline"
                      >
                        {e.accessCode}
                      </Link>
                      <p className="text-xs text-slate-500">
                        {new Date(e.createdAt).toLocaleDateString('tr-TR')}
                        {e.anonymousMode && ' · Anonim'}
                      </p>
                    </div>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                      {e._count.attempts} katılım
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* Activity feed */}
        {data.recentActivity.length > 0 && (
          <Card className="mt-6">
            <h2 className="text-lg font-semibold text-slate-900">
              Son Aktiviteler
            </h2>
            <ul className="mt-4 space-y-2 text-sm">
              {data.recentActivity.map((a) => (
                <li
                  key={a.id}
                  className="flex items-center justify-between gap-2"
                >
                  <span className="text-slate-700">
                    {EVENT_LABELS[a.eventType] ?? a.eventType}
                  </span>
                  <span className="text-xs text-slate-500">
                    {new Date(a.createdAt).toLocaleString('tr-TR')}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Hızlı aksiyon kartları — sadece OWNER görür */}
        {data.currentMember.role === 'OWNER' && (
          <div className="mt-8 grid gap-4 md:grid-cols-4">
            <ActionCard
              icon="📝"
              title="Yeni Sınav"
              desc="Sektör şablonu seç, davet linki üret."
              onClick={() => setLocation('/yonetici/sinavlar/yeni')}
            />
            <ActionCard
              icon="👥"
              title="Ekip Üyeleri"
              desc="Yönetici davet et ve yetkilerini yönet."
              onClick={() => setLocation('/yonetici/davetler')}
            />
            <ActionCard
              icon="📊"
              title="Tüm Sınavlar"
              desc="Davet linklerini paylaş, sonuçları gör."
              onClick={() => setLocation('/yonetici/sinavlar')}
            />
            <ActionCard
              icon="📈"
              title="Kohort Analitiği"
              desc="Sınavlar arası ekip gelişimi ve aday ilerlemeleri."
              onClick={() => setLocation('/yonetici/kohort')}
            />
          </div>
        )}
      </main>

      {/* Floating chatbot widget (Spec 6.5) */}
      <ChatWidget />
    </div>
  );
}

function CountCard({
  icon,
  label,
  value,
  href,
}: {
  icon: string;
  label: string;
  value: number;
  href?: string;
}): JSX.Element {
  const content = (
    <>
      <div className="text-2xl" aria-hidden="true">
        {icon}
      </div>
      <p className="mt-2 text-xs uppercase tracking-wider text-slate-500">
        {label}
      </p>
      <p className="mt-1 text-3xl font-bold text-slate-900">{value}</p>
    </>
  );
  const cls =
    'rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md';
  if (href) {
    return (
      <Link href={href} className={`${cls} block`}>
        {content}
      </Link>
    );
  }
  return <div className={cls}>{content}</div>;
}

function Detail({
  label,
  value,
  hint,
  highlight,
}: {
  label: string;
  value: string;
  hint?: string;
  highlight?: boolean;
}): JSX.Element {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
      <p
        className={`mt-1 text-xl font-bold ${
          highlight ? 'text-green-700' : 'text-slate-900'
        }`}
      >
        {value}
      </p>
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

function ActionCard({
  icon,
  title,
  desc,
  onClick,
}: {
  icon: string;
  title: string;
  desc: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group rounded-xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="text-2xl" aria-hidden="true">
        {icon}
      </div>
      <h3 className="mt-3 text-base font-semibold text-slate-900">{title}</h3>
      <p className="mt-1 text-sm text-slate-600">{desc}</p>
      <span className="mt-3 inline-block text-sm font-medium text-blue-900 group-hover:underline">
        Aç →
      </span>
    </button>
  );
}
